import { useState } from "react";
import { useTranslation } from "react-i18next";
import { addDays, startOfToday } from "date-fns";
import { theme } from "../../../theme";
import Modal from "../Modal";
import Alert from "../Alert";
import DatePicker from "../filters/DatePicker";
import TextField from "../../atoms/inputs/TextField";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import Checkbox from "../../atoms/inputs/Checkbox";
import Divider from "../../atoms/Divider";
import Body from "../../atoms/typography/Body";
import Small from "../../atoms/typography/Small";
import { HorizontalSpacer } from "../../atoms/Spacers";
import type {
  SaveExemptionResult,
  ExemptionableRuleModel,
} from "../../../models/InvoiceAssessment";
import { useSaveExemption } from "../../../graphql/hooks/invoices/useSaveExemption";

interface Props {
  rule: ExemptionableRuleModel;
  clientIdentifier: string;
  recipientIdentifier?: string;
  recipientName?: string;
  onClose: () => void;
  onCreated: () => void;
}

const parseNumber = (value: string) => {
  const normalized = value.trim().replace(",", ".");
  return normalized !== "" && !isNaN(Number(normalized))
    ? normalized
    : undefined;
};

const CreateWhitelistModal: React.FC<Props> = ({
  rule,
  clientIdentifier,
  recipientIdentifier,
  recipientName,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { defaultEndDateDays, relationType, requiredValueType } =
    rule.exemptionOption;
  const requiresValue = requiredValueType === "NUMBER";
  const canChooseRecipient =
    relationType === "ISSUER_OR_RECIPIENT" && !!recipientIdentifier;

  const [endDate, setEndDate] = useState<Date | undefined>(
    defaultEndDateDays > 0
      ? addDays(startOfToday(), defaultEndDateDays)
      : undefined,
  );
  const [value, setValue] = useState("");
  const [comment, setComment] = useState("");
  const [includeRecipient, setIncludeRecipient] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const parsedValue = parseNumber(value);
  const valueError =
    requiresValue && value !== "" && !parsedValue
      ? t("invoiceAssessment.whitelistInvalidValue")
      : undefined;
  const isValid =
    comment.trim() !== "" && (!requiresValue || parsedValue !== undefined);

  const appliesToRecipient =
    relationType === "RECIPIENT_REQUIRED" ||
    (canChooseRecipient && includeRecipient);

  const { saveExemption, loading } = useSaveExemption({
    onCompleted: (result: SaveExemptionResult) => {
      if (result.invoice?.saveExemption?.success) {
        onCreated();
      } else {
        setErrorMessage(t("invoiceAssessment.whitelistError"));
      }
    },
    onError: () => setErrorMessage(t("invoiceAssessment.whitelistError")),
  });

  const handleSave = () => {
    if (!isValid || loading) return;
    setErrorMessage("");
    saveExemption({
      variables: {
        input: {
          clientIdentifier,
          ruleType: rule.ruleType,
          comment: comment.trim(),
          value: requiresValue ? parsedValue : undefined,
          endDate,
          recipientIdentifier: appliesToRecipient
            ? recipientIdentifier
            : undefined,
        },
      },
    }).catch(() => {
      setErrorMessage(t("invoiceAssessment.whitelistError"));
    });
  };

  return (
    <Modal
      onClose={onClose}
      closeIcon
      width={600}
      title={t("invoiceAssessment.createWhitelist")}
      actionButtons={{
        defaultButton: {
          buttonTitle: t("common.cancel"),
          onClick: onClose,
        },
        actionButton: {
          buttonTitle: t("invoiceAssessment.createWhitelist"),
          onClick: handleSave,
          loading,
          disabled: !isValid || loading,
        },
      }}
    >
      <Body fontweight="500">
        {t("invoiceAssessment.createWhitelistForRule", {
          ruleName: rule.ruleName,
        })}
      </Body>

      {canChooseRecipient && (
        <>
          <HorizontalSpacer spacing={1} />
          <Checkbox
            title={t("invoiceAssessment.whitelistInCombinationWithRecipient", {
              recipientName: recipientName ?? "",
              recipientIdentifier,
            })}
            checked={includeRecipient}
            onChange={() => setIncludeRecipient(!includeRecipient)}
          />
        </>
      )}

      <HorizontalSpacer spacing={1} />
      <DatePicker
        title={t("invoiceAssessment.whitelistValidUntil")}
        value={endDate}
        disableDatesBeforeToday
        onChange={setEndDate}
      />

      {requiresValue && (
        <>
          <HorizontalSpacer spacing={1} />
          <TextField
            label={t("invoiceAssessment.whitelistNewValue")}
            placeholder={t("invoiceAssessment.whitelistNewValuePlaceholder")}
            value={value}
            onChange={setValue}
            errorMessage={valueError}
            fullWidth
          />
        </>
      )}

      <HorizontalSpacer spacing={1} />
      <MultilineTextField
        label={t("common.comment")}
        placeholder={t("invoiceAssessment.whitelistCommentPlaceholder")}
        value={comment}
        onChange={setComment}
      />

      {errorMessage && (
        <>
          <HorizontalSpacer spacing={1} />
          <Alert
            type="error"
            title={t("errors.somethingWentWrong")}
            message={errorMessage}
          />
        </>
      )}

      <HorizontalSpacer spacing={1} />
      <Divider />
      <HorizontalSpacer spacing={0.5} />
      <Small fontweight="400" color={theme.colors.textSecondary}>
        {t("invoiceAssessment.whitelistInformation")}
      </Small>
    </Modal>
  );
};

export default CreateWhitelistModal;
